import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Coins, Music, Smartphone, Megaphone } from "lucide-react";
import { api } from "@/lib/api";
import { useUserCredits } from "@/hooks/useUserCredits";
import type { CreditBalances, ServiceCreditUsage } from "./types";

// CreditHud — the prominent balance readout that sits right above the
// storyboard. Total spendable credits plus the per-service breakdown
// (Corrido / TikTok story / Commercials & ads), computed from the
// CreditLedger. Our own engines are the ONLY credit sinks — the old A2E
// accounting is gone.

type HudService = "corrido" | "story" | "ads";

const EMPTY_USAGE = (service: HudService): ServiceCreditUsage => ({
  service, spent: 0, refunded: 0, runs: 0,
});

export default function CreditHud({ isAuthenticated }: { isAuthenticated: boolean }) {
  const { t } = useTranslation();
  const { balance, serviceCosts } = useUserCredits(isAuthenticated);
  const [breakdown, setBreakdown] = useState<Pick<CreditBalances, HudService>>({
    corrido: EMPTY_USAGE("corrido"),
    story: EMPTY_USAGE("story"),
    ads: EMPTY_USAGE("ads"),
  });

  // Recompute the service split whenever the balance changes (the shared hook
  // already refreshes it after every launch/render).
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    void (async () => {
      const response = await api.getCreditLedger(200);
      if (cancelled || !response.success || !response.data) return;
      const next = { corrido: EMPTY_USAGE("corrido"), story: EMPTY_USAGE("story"), ads: EMPTY_USAGE("ads") };
      for (const entry of response.data.entries) {
        if (entry.service !== "corrido" && entry.service !== "story" && entry.service !== "ads") continue;
        const bucket = next[entry.service];
        if (entry.entry_type === "charge") { bucket.spent += Math.abs(entry.delta); bucket.runs += 1; }
        if (entry.entry_type === "refund") { bucket.refunded += Math.abs(entry.delta); }
      }
      setBreakdown(next);
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, balance]);

  const rows: Array<{ usage: ServiceCreditUsage; label: string; icon: React.ReactNode; costPerRun?: number; from?: boolean }> = [
    { usage: breakdown.corrido, label: t("studio.corridoMusicVideos"), icon: <Music className="h-3.5 w-3.5" />, costPerRun: serviceCosts?.corrido },
    { usage: breakdown.story, label: t("studio.tiktokStories"), icon: <Smartphone className="h-3.5 w-3.5" />, costPerRun: serviceCosts?.story },
    // Ads is a ladder, not one price: the chip shows the cheapest spot ("from").
    { usage: breakdown.ads, label: t("studio.commercials"), icon: <Megaphone className="h-3.5 w-3.5" />, costPerRun: serviceCosts?.ads?.spot_15, from: true },
  ];

  return (
    <motion.aside
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card/80 px-4 py-3 shadow-[0_14px_30px_rgba(0,0,0,0.28)]" data-testid="studio-credit-hud" aria-live="polite" >
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-amber-400/25 to-primary/20">
          <Coins className="h-5 w-5 text-amber-400" />
        </div>
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-muted-foreground">{t("studio.credits")}</div>
          {/* Signed out, the big bold "Sign in" read as a button that did
              nothing when tapped (2026-09-24); it is a status, so it is set
              like one. The console's own chat carries the real sign-in. */}
          <div className={isAuthenticated ? "text-xl font-extrabold leading-tight text-foreground" : "text-base font-semibold leading-tight text-muted-foreground"}>
            {isAuthenticated ? (balance === null ? "—" : balance.toLocaleString()) : t("studio.signIn")}
          </div>
        </div>
      </div>

      {/* Signed out on a phone, three "No runs charged yet" rows were ~130px of
          noise above the console; they come back from sm up and once signed in. */}
      <div className={`${isAuthenticated ? "flex" : "hidden sm:flex"} flex-wrap items-center gap-4`}>
        {rows.map(({ usage, label, icon, costPerRun, from }) => (
          <div key={usage.service} className="text-xs" data-testid={`studio-credit-${usage.service}`}>
            <div className="flex items-center gap-1.5 font-semibold text-foreground">
              {icon} {label}
              {typeof costPerRun === "number" && (
                <span className="rounded-full border border-border bg-background/60 px-1.5 py-0.5 text-[11px] font-bold text-amber-400">
                  {costPerRun === 0 ? t("studio.free") : from ? t("studio.fromPerRun", { cost: costPerRun.toLocaleString() }) : t("studio.perRun", { cost: costPerRun.toLocaleString() })}
                </span>
              )}
            </div>
            <div className="mt-0.5 text-muted-foreground">
              {usage.runs === 0
                ? t("studio.noRunsCharged")
                : `${t("studio.runsSummary", { count: usage.runs, spent: usage.spent.toLocaleString() })}${usage.refunded ? t("studio.refundedSuffix", { refunded: usage.refunded.toLocaleString() }) : ""}`}
            </div>
          </div>
        ))}
        {/* Buy Credits removed — no pricing system yet. */}
      </div>
    </motion.aside>
  );
}
